// Core 100 — a one-page cheat sheet of the highest-frequency A1 words, jotted
// like notes on paper and grouped into colour-coded blocks. Pairs with the First
// Words starter (that's the phrases; this is the words underneath). Tap a word to
// hear it; hide the English to quiz yourself.
import { speakButtonHTML } from "../lib/audio.js";

const BLOCKS = [
  { title: "Courtesy & basics", color: "text-sage-700", words: [["olá", "hello"], ["adeus", "goodbye"], ["sim", "yes"], ["não", "no"], ["obrigado", "thank you"], ["por favor", "please"], ["desculpe", "sorry"]] },
  { title: "Question words", color: "text-rose-600", words: [["que", "what"], ["quem", "who"], ["onde", "where"], ["quando", "when"], ["como", "how"], ["porquê", "why"], ["quanto", "how much"], ["qual", "which"]] },
  { title: "Pronouns", color: "text-indigo-600", words: [["eu", "I"], ["tu", "you"], ["ele", "he"], ["ela", "she"], ["você", "you (formal)"], ["nós", "we"], ["vós", "you all"], ["eles", "they"]] },
  { title: "The big verbs", color: "text-amber-700", words: [["ser", "to be"], ["estar", "to be"], ["ter", "to have"], ["fazer", "to do/make"], ["ir", "to go"], ["poder", "to be able"], ["querer", "to want"], ["ver", "to see"], ["dar", "to give"], ["saber", "to know"], ["dizer", "to say"], ["vir", "to come"]] },
  { title: "Time words", color: "text-sky-700", words: [["hoje", "today"], ["amanhã", "tomorrow"], ["ontem", "yesterday"], ["agora", "now"], ["sempre", "always"], ["nunca", "never"], ["antes", "before"], ["depois", "after"], ["cedo", "early"], ["tarde", "late"]] },
  { title: "Connectors", color: "text-teal-700", words: [["e", "and"], ["ou", "or"], ["mas", "but"], ["porque", "because"], ["também", "also"], ["se", "if"], ["quando", "when"], ["embora", "although"]] },
  { title: "Prepositions", color: "text-violet-700", words: [["de", "of / from"], ["a", "to"], ["em", "in / on"], ["com", "with"], ["por", "for / by"], ["para", "for"], ["sem", "without"], ["sobre", "on / about"], ["entre", "between"], ["até", "until"]] },
  { title: "Everyday nouns", color: "text-stone-600", words: [["a casa", "house"], ["a água", "water"], ["o dia", "day"], ["o tempo", "time / weather"], ["a pessoa", "person"], ["a coisa", "thing"], ["o homem", "man"], ["a mulher", "woman"], ["o ano", "year"], ["a vida", "life"]] },
  { title: "Useful adjectives", color: "text-emerald-700", words: [["bom", "good"], ["mau", "bad"], ["grande", "big"], ["pequeno", "small"], ["novo", "new"], ["muito", "a lot"], ["pouco", "a little"], ["fácil", "easy"], ["difícil", "hard"], ["mesmo", "same"]] },
  { title: "Little words", color: "text-sage-700", words: [["muito", "very"], ["mais", "more"], ["menos", "less"], ["bem", "well"], ["mal", "badly"], ["aqui", "here"], ["ali", "there"], ["assim", "like this"]] },
  { title: "Numbers 1–10", color: "text-rose-600", words: [["um", "1"], ["dois", "2"], ["três", "3"], ["quatro", "4"], ["cinco", "5"], ["seis", "6"], ["sete", "7"], ["oito", "8"], ["nove", "9"], ["dez", "10"]] },
];

function blockHTML(b) {
  const rows = b.words
    .map(
      ([pt, en]) => `
      <div class="flex items-baseline justify-between gap-2 py-0.5">
        <span class="text-sage-800">${pt}${speakButtonHTML(pt.replace(/^(o|a) /, ""))}</span>
        <span class="c100-en text-sm text-sage-500 text-right">${en}</span>
      </div>`
    )
    .join("");
  return `
    <div class="sketch-box-sm bg-white/30 p-3 mb-4 break-inside-avoid">
      <h3 class="${b.color} text-xl sketch-underline inline-block mb-1">${b.title} <span class="opacity-50 text-sm">· ${b.words.length}</span></h3>
      <div>${rows}</div>
    </div>`;
}

export function core100View(app) {
  const total = BLOCKS.reduce((n, b) => n + b.words.length, 0);
  app.innerHTML = `
    <section class="max-w-4xl mx-auto fade-in">
      <div class="flex items-center justify-between gap-3 mb-1 flex-wrap">
        <h2 class="text-4xl font-script text-sage-800">Core ${total} Words 🗺️</h2>
        <button id="c100-toggle" class="text-sm px-3 py-1.5 rounded-full border border-sage-200 text-sage-600 hover:border-sage-400" style="font-family:Inter,sans-serif">Hide English</button>
      </div>
      <p class="text-sage-600 mb-4" style="font-family:Inter,sans-serif">The highest-frequency words in Portuguese, grouped so you find and revisit them fast. Tap 🔊 to hear a word; hide the English to test yourself.</p>
      <div class="paper sketch-box p-4 sm:p-6 font-hand text-lg">
        <div class="columns-1 sm:columns-2 lg:columns-3 gap-4">
          ${BLOCKS.map(blockHTML).join("")}
        </div>
      </div>
      <p class="text-xs text-sage-400 mt-4" style="font-family:Inter,sans-serif">Nouns show their article (o/a) so you learn the gender with the word.</p>
    </section>`;

  const sec = app.querySelector("section");
  const btn = app.querySelector("#c100-toggle");
  let hidden = false;
  btn.addEventListener("click", () => {
    hidden = !hidden;
    sec.querySelectorAll(".c100-en").forEach((e) => e.classList.toggle("invisible", hidden));
    btn.textContent = hidden ? "Show English" : "Hide English";
  });
  window.scrollTo({ top: 0, behavior: "smooth" });
}
